import React from 'react';
import { Section } from '../ui/Section';
import { Button } from '../ui/Button';
import { Check, Gift, BookOpen, ArrowRight } from 'lucide-react';
export function Bonuses() {
  const bonuses = [{
    id: 1,
    title: 'Home Auction Deal Scanner Checklist',
    value: '$17',
    image: 'https://cdn.magicpatterns.com/uploads/oxPzgDkQmKFBvWyrjdSF4Y/Home_auction_deal_scanner_checklist.png',
    hook: 'Find auction homes in your area without wasting hours searching.',
    description: ['Most beginners waste days scrolling around with no real system, and they miss the best deals because someone else spots them first.', 'This simple step-by-step checklist fixes that.', 'It shows you exactly where to look, how to set up smart alerts, and how to spot promising below-market homes faster.', "It's built so you have a repeatable, beginner-friendly system, even if you've never looked at an auction property in your life."],
    forWho: ["You don't know where to even start looking for auction homes", 'You want to catch good deals early before other buyers do', 'You want a simple system instead of random searching'],
    inside: ['A step-by-step checklist to find auction homes in your area', 'How to set up smart alerts so deals come to you automatically', 'What to track so you can spot strong below-market homes fast', 'A repeatable routine you can use every single week'],
    result: 'You know exactly where to look, what to track, and how to catch the best deals early.'
  }, {
    id: 2,
    title: 'Hidden Risk & Debt Assessment Checklist',
    value: '$17',
    image: 'https://cdn.magicpatterns.com/uploads/fYi8RmbCujGRDbx3x5VYfD/Hidden_risk__debt_assement_checklist.png',
    hook: 'Spot the warning signs before you bid, so a cheap house never becomes an expensive problem.',
    description: ['A house can look like an amazing deal and still hide debts, liens, or repair problems that cost you a fortune later.', 'This practical checklist helps you avoid those costly mistakes.', 'It walks you through the warning signs to look for before you bid — hidden debts, title issues, repair red flags, and neighborhood concerns.', 'So you can quickly tell the difference between a true great deal and a risky trap.'],
    forWho: ['You worry about getting stuck with hidden costs', 'You want to protect your money on your first purchase', 'You want to bid with confidence instead of fear'],
    inside: ['How to check for possible hidden debts and liens', 'The title and paperwork red flags to watch for', 'Repair and condition warning signs that signal trouble', 'Neighborhood and deal-breaker checks before you commit'],
    result: 'You protect your money, avoid nasty surprises, and bid knowing exactly what the risks are.'
  }, {
    id: 3,
    title: 'Bidding Process Decoded Checklist',
    value: '$17',
    image: 'https://cdn.magicpatterns.com/uploads/pj9mGqugnG97a2wpJRfDQL/Bidding_process_decoded_checkslit.png',
    hook: 'A calm, clear plan for before, during, and after the auction.',
    description: ["For most beginners, the bidding itself is the scariest part — the rules feel confusing and it's easy to panic or overbid.", 'This beginner-friendly checklist breaks the whole thing into simple, easy-to-follow steps.', 'It shows you exactly what to do before the auction, during the bidding, and right after you win.', 'No confusing rules. Just a calm, clear plan so your first auction feels simple.'],
    forWho: ["You've never been to an auction and don't know the rules", 'You worry about panicking or overbidding in the moment', 'You want a simple plan you can follow step by step'],
    inside: ['Exactly what to prepare before the auction starts', 'A simple bidding strategy so you never overpay', 'What to do the moment you win the property', 'The key paperwork and decisions that actually matter'],
    result: 'You feel prepared and calm, and you bid the smart way instead of making expensive mistakes.'
  }];
  return <Section theme="black" className="border-b border-slate-100" data-id="element-12">
      {/* Opening Bridge */}
      <div className="max-w-3xl mx-auto text-center mb-20" data-id="element-13">
        <div className="flex items-center justify-center gap-4 mb-6" data-id="element-14">
          <div className="h-1 w-8 bg-orange-500 rounded-full" data-id="element-15"></div>
          <span className="text-orange-600 text-xs font-bold tracking-wider uppercase" data-id="element-16">
            Exclusive Bonuses
          </span>
          <div className="h-1 w-8 bg-orange-500 rounded-full" data-id="element-17"></div>
        </div>

        <h2 className="text-3xl md:text-5xl font-display font-bold mb-8 text-slate-900 leading-tight" data-id="element-18">
          Exclusive Beginner Bonuses to Help You{' '}
          <span className="text-orange-500" data-id="element-19">Buy Smarter</span>.
        </h2>

        <div className="space-y-6 text-lg md:text-xl text-slate-600 font-sans leading-relaxed" data-id="element-20">
          <p data-id="element-21">
            Get the exact checklists that help first-time buyers{' '}
            <strong className="text-slate-900 font-semibold" data-id="element-22">
              find deals, avoid hidden risks, and bid with confidence
            </strong>{' '}
            on their first home auction purchase.
          </p>
          <p data-id="element-23">
            When you grab the guide right now, all three of these
            beginner-friendly checklists are included — completely free — so you
            have a clear path to your first house for up to 50% off.
          </p>
          <p data-id="element-24">
            No confusing words. No fancy jargon. Just simple shortcuts that save
            you time, money, and costly mistakes, even if it's your very first
            auction.
          </p>
        </div>
      </div>

      {/* Bonus Cards */}
      <div className="space-y-16 max-w-5xl mx-auto mb-24" data-id="element-25">
        {bonuses.map(bonus => <div key={bonus.id} className="bg-white rounded-3xl border border-slate-200 shadow-soft-lg overflow-hidden flex flex-col" data-id="element-26">
            {/* Image Top — full horizontal cover, shown complete on all viewports */}
            <div className="w-full bg-slate-50 p-4 md:p-6 flex items-center justify-center border-b border-slate-100 relative" data-id="element-27">
              <img src={bonus.image} alt={bonus.title} className="w-full h-auto object-contain relative z-10 rounded-xl shadow-sm border border-slate-200" data-id="element-28" />
            </div>

            {/* Content Below */}
            <div className="w-full p-8 md:p-12" data-id="element-29">
              <div className="flex items-center gap-3 mb-4" data-id="element-30">
                <div className="bg-orange-100 p-1.5 rounded-lg" data-id="element-31">
                  <Gift className="w-5 h-5 text-orange-500" data-id="element-32" />
                </div>
                <span className="text-orange-600 text-sm font-bold tracking-wider uppercase" data-id="element-33">
                  Free Bonus #{bonus.id}
                </span>
              </div>

              <h3 className="text-2xl md:text-3xl font-display font-bold text-slate-900 mb-4 leading-tight" data-id="element-34">
                {bonus.title}
              </h3>

              <div className="inline-block px-4 py-1.5 bg-green-50 rounded-full border border-green-200 text-green-700 text-sm font-bold mb-8" data-id="element-35">
                Valued at {bonus.value} — Yours FREE today.
              </div>

              <p className="text-xl font-display font-bold text-slate-800 mb-6 border-l-4 border-orange-500 pl-4 bg-slate-50 py-2 rounded-r-lg" data-id="element-36">
                "{bonus.hook}"
              </p>

              <div className="space-y-4 text-slate-600 leading-relaxed mb-8" data-id="element-37">
                {bonus.description.map((p, i) => <p key={i} data-id="element-38">{p}</p>)}
              </div>

              <div className="mb-8" data-id="element-39">
                <h4 className="text-slate-900 font-bold mb-4 uppercase tracking-wider text-sm" data-id="element-40">
                  This bonus is for you if:
                </h4>
                <ul className="space-y-3" data-id="element-41">
                  {bonus.forWho.map((item, i) => <li key={i} className="flex gap-3 items-start" data-id="element-42">
                      <ArrowRight className="w-4 h-4 text-orange-500 shrink-0 mt-1" data-id="element-43" />
                      <span className="text-slate-600" data-id="element-44">{item}</span>
                    </li>)}
                </ul>
              </div>

              <div className="mb-8 bg-slate-50 p-6 rounded-2xl border border-slate-200" data-id="element-45">
                <h4 className="text-slate-900 font-bold mb-4 uppercase tracking-wider text-sm" data-id="element-46">
                  Inside you'll get:
                </h4>
                <ul className="space-y-3" data-id="element-47">
                  {bonus.inside.map((item, i) => <li key={i} className="flex gap-3 items-start" data-id="element-48">
                      <Check className="w-5 h-5 text-orange-500 shrink-0 mt-0.5" data-id="element-49" />
                      <span className="text-slate-600" data-id="element-50">{item}</span>
                    </li>)}
                </ul>
              </div>

              <div data-id="element-51">
                <h4 className="text-orange-600 font-bold mb-2 uppercase tracking-wider text-sm" data-id="element-52">
                  The Result:
                </h4>
                <p className="text-slate-900 font-medium text-lg leading-relaxed" data-id="element-53">
                  {bonus.result}
                </p>
              </div>
            </div>
          </div>)}
      </div>

      {/* Stack Summary Section */}
      <div className="max-w-3xl mx-auto mb-24" data-id="element-54">
        <h3 className="text-2xl md:text-4xl font-display font-bold text-center text-slate-900 mb-10" data-id="element-55">
          Here's Everything You're Getting Today
        </h3>

        <div className="bg-white rounded-3xl border border-slate-200 shadow-soft-lg overflow-hidden" data-id="element-56">
          {/* Header bar */}
          <div className="bg-orange-500 px-6 py-4 flex items-center justify-between" data-id="element-57">
            <span className="text-white text-xs md:text-sm font-bold tracking-wider uppercase" data-id="element-58">
              What You Get
            </span>
            <span className="text-white text-xs md:text-sm font-bold tracking-wider uppercase" data-id="element-59">
              Value
            </span>
          </div>

          {/* Rows */}
          <ul className="divide-y divide-slate-100" data-id="element-60">
            <li className="flex items-center justify-between gap-4 px-5 md:px-6 py-5 hover:bg-slate-50 transition-colors" data-id="element-61">
              <div className="flex items-center gap-4" data-id="element-62">
                <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-orange-100 shrink-0" data-id="element-63">
                  <BookOpen className="w-5 h-5 text-orange-500" data-id="element-64" />
                </span>
                <span className="font-semibold text-slate-900 text-sm md:text-base" data-id="element-65">
                  The 2026 Home Auction Secrets Blueprint for Beginners
                </span>
              </div>
              <span className="text-slate-500 font-semibold shrink-0" data-id="element-66">$47</span>
            </li>

            {['BONUS #1: Home Auction Deal Scanner Checklist', 'BONUS #2: Hidden Risk & Debt Assessment Checklist', 'BONUS #3: Bidding Process Decoded Checklist'].map(label => <li key={label} className="flex items-center justify-between gap-4 px-5 md:px-6 py-5 hover:bg-slate-50 transition-colors" data-id="element-67">
                <div className="flex items-center gap-4" data-id="element-68">
                  <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-orange-100 shrink-0" data-id="element-69">
                    <Gift className="w-5 h-5 text-orange-500" data-id="element-70" />
                  </span>
                  <span className="text-slate-700 text-sm md:text-base" data-id="element-71">
                    {label}
                  </span>
                </div>
                <span className="shrink-0 inline-flex items-center gap-1.5" data-id="element-72">
                  <span className="text-slate-400 text-sm line-through" data-id="element-73">
                    $17
                  </span>
                  <span className="bg-green-100 text-green-700 text-xs font-bold uppercase px-2 py-1 rounded-md" data-id="element-74">
                    Free
                  </span>
                </span>
              </li>)}

            {/* Total value */}
            <li className="flex items-center justify-between gap-4 px-5 md:px-6 py-5 bg-slate-50" data-id="element-75">
              <span className="font-display font-bold text-lg md:text-xl text-slate-900" data-id="element-76">
                Total Value
              </span>
              <span className="font-display font-bold text-lg md:text-xl text-slate-400 line-through" data-id="element-77">
                $98
              </span>
            </li>
          </ul>

          {/* Price today — standout footer */}
          <div className="bg-orange-50 border-t-2 border-orange-200 px-6 py-8 flex flex-col sm:flex-row items-center sm:justify-between gap-3 text-center sm:text-left" data-id="element-78">
            <div data-id="element-79">
              <p className="text-orange-700 font-display font-bold text-lg md:text-xl uppercase tracking-wide" data-id="element-80">
                Your Price Today
              </p>
              <p className="text-green-700 text-xl md:text-2xl font-display font-bold mt-1 uppercase tracking-wide" data-id="element-81">
                94% OFF TODAY
              </p>
            </div>
            <div className="flex items-start" data-id="element-82">
              <span className="text-2xl md:text-3xl font-display font-bold text-orange-600 mt-1" data-id="element-83">
                $
              </span>
              <span className="text-5xl md:text-6xl font-display font-bold text-orange-600 tracking-tight" data-id="element-84">
                6.36
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Closing Bridge Line */}
      <div className="max-w-3xl mx-auto text-center" data-id="element-85">
        <h2 className="text-3xl md:text-4xl font-display font-bold mb-8 text-slate-900 leading-tight" data-id="element-86">
          This Isn't Just a Guide —{' '}
          <span className="text-orange-500" data-id="element-87">
            It's Your Whole Path to a First Home.
          </span>
        </h2>

        <p className="text-slate-900 font-bold text-2xl mb-8" data-id="element-88">
          It's a complete starting system:
        </p>

        <ul className="space-y-4 text-left max-w-xl mx-auto mb-10" data-id="element-89">
          <li className="flex gap-3 items-start" data-id="element-90">
            <Check className="w-6 h-6 text-orange-500 shrink-0 mt-0.5" data-id="element-91" />
            <span className="text-lg text-slate-600" data-id="element-92">
              <strong className="text-slate-900" data-id="element-93">The Guide</strong> — navigate
              the hidden auction market.
            </span>
          </li>
          <li className="flex gap-3 items-start" data-id="element-94">
            <Check className="w-6 h-6 text-orange-500 shrink-0 mt-0.5" data-id="element-95" />
            <span className="text-lg text-slate-600" data-id="element-96">
              <strong className="text-slate-900" data-id="element-97">Bonus #1</strong> — find the
              best deals fast.
            </span>
          </li>
          <li className="flex gap-3 items-start" data-id="element-98">
            <Check className="w-6 h-6 text-orange-500 shrink-0 mt-0.5" data-id="element-99" />
            <span className="text-lg text-slate-600" data-id="element-100">
              <strong className="text-slate-900" data-id="element-101">Bonus #2</strong> — spot hidden
              risks before you bid.
            </span>
          </li>
          <li className="flex gap-3 items-start" data-id="element-102">
            <Check className="w-6 h-6 text-orange-500 shrink-0 mt-0.5" data-id="element-103" />
            <span className="text-lg text-slate-600" data-id="element-104">
              <strong className="text-slate-900" data-id="element-105">Bonus #3</strong> — bid with a
              calm, step-by-step plan.
            </span>
          </li>
        </ul>

        <p className="text-orange-600 font-display font-bold text-2xl mb-12" data-id="element-106">
          All of it today for just $6.36.
        </p>

        <Button className="md:px-12 py-5 text-xl mb-6" onClick={e => {
        e.preventDefault();
        document.getElementById('offer')?.scrollIntoView({
          behavior: 'smooth'
        });
      }} data-id="element-107">
          Yes! Get My First House 50% Off
        </Button>

        <div className="text-slate-500 text-sm flex items-center justify-center gap-2 font-medium" data-id="element-108">
          <Check className="w-4 h-4 text-orange-500" data-id="element-109" />
          Instant PDF Download. All 4 resources delivered immediately.
        </div>
      </div>
    </Section>;
}